import React, { useEffect } from "react";
import styled, { useTheme } from "styled-components";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import joynLogo from "~/assets/i/logo.svg";
import { Avatar, Button, Dropdown, Grid, Icon, InputToggle, Link, Picture, Spinner, Text } from "~/components-shared";
import notificationSelectors from "~/store/notifications/selectors";
import notificationsActionCreators from "~/store/notifications/actions";
import { AuthoritiesEnum, NotificationStatusEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import { useTranslation } from "react-i18next";
import { useAbac } from "react-abac";
import Markdown from "~/components/Markdown";
import moment from "moment";
const Notifications = styled.div`
  position: absolute;
  background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.danger.main;
}};
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.background.primary;
}};
  font-size: 12px;
  font-weight: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.typography.fontWeight.bold;
}};
  top: -2px;
  right: -2px;
  transition: transform 300ms ease-in-out;
  ${_ref4 => {
  let {
    count
  } = _ref4;
  return count <= 0 && "transform: scale(0)";
}}
`;
const Dot = styled.i`
  background-color: ${_ref5 => {
  let {
    active,
    theme
  } = _ref5;
  return active ? theme.colors.primary.main : theme.colors.background.dark;
}};
  border-radius: 50%;
  width: 6px;
  height: 6px;
  display: block;
`;
const StyledInputToggle = styled(InputToggle)`
  font-size: 10px;
  vertical-align: top;
  line-height: 0;
`;
const StyledGrid = styled(Grid)`
  border-bottom: 1px solid ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.colors.background.dark;
}};
  position: sticky;
  top: 0;
  background-color: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.colors.background.primary;
}};
  z-index: 2;
`;
const StyledButton = styled(Button)`
  border-bottom: 1px solid ${_ref8 => {
  let {
    theme
  } = _ref8;
  return theme.colors.background.dark;
}};
  border-radius: 0;

  &:before {
    border-radius: 0;
  }
`;
const scrollPaneStyle = {
  overflow: "initial",
  height: "initial"
};
const NotificationDropdown = _ref9 => {
  let {
    direction = "bottomLeft",
    ...rest
  } = _ref9;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const notifications = useSelector(notificationSelectors.getNotifications);
  const {
    hasAfter
  } = useSelector(notificationSelectors.getPagination);
  const summary = useSelector(notificationSelectors.getSummary);
  const filter = useSelector(notificationSelectors.getFilter);
  const status = useSelector(notificationSelectors.getStatus);
  const dispatch = useDispatch();
  const {
    userHasPermissions
  } = useAbac();
  const history = useHistory();
  const handleToggleRead = notification => {
    if (userHasPermissions(AuthoritiesEnum.NOTIFICATIONS_WRITE)) {
      dispatch(notification.status === NotificationStatusEnum.UNREAD ? notificationsActionCreators.setReadStarted(notification.id) : notificationsActionCreators.setUnReadStarted(notification.id));
    }
  };
  const handleReadAll = () => {
    dispatch(notificationsActionCreators.setAllReadStarted());
  };
  const handleClick = notification => {
    dispatch(notificationsActionCreators.setReadStarted(notification.id));
    if (notification.link) {
      if (notification.link.startsWith("/")) {
        history.push(notification.link);
      } else {
        window.open(notification.link, "_blank");
      }
    }
  };
  const handleMemberClick = (memberId, e) => {
    e.stopPropagation();
    history.push(RoutesEnum.MEMBER.replace(":memberId", memberId));
  };
  const handleFilter = newFilter => {
    dispatch(notificationsActionCreators.filtered(newFilter));
  };
  const handleLoadMore = () => {
    dispatch(notificationsActionCreators.loadedMore());
  };
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(notificationsActionCreators.fetchStarted());
    }
  }, [dispatch, status]);
  return /*#__PURE__*/React.createElement(Dropdown, {
    direction: direction,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("NotificationDropdown.notifications"),
    handlerProps: {
      s: {
        px: 3.5,
        py: 3
      },
      rounded: true,
      shadow: true,
      ...rest
    },
    handler: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
      src: "bell",
      scale: 5
    }), /*#__PURE__*/React.createElement(Notifications, {
      count: summary?.unreadCount || 0
    }, summary?.unreadCount && summary?.unreadCount > 9 ? "9+" : summary?.unreadCount || 0))
  }, _ref10 => {
    let {
      isOpen,
      onItemClick
    } = _ref10;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen,
      width: "360px",
      id: "notificationList"
    }, /*#__PURE__*/React.createElement(StyledGrid, {
      cols: 2,
      gap: 5,
      align: "center",
      s: {
        p: 3
      }
    }, /*#__PURE__*/React.createElement(Text, {
      element: "h4"
    }, t("NotificationDropdown.notifications"), status === StatusStateEnum.LOADING && /*#__PURE__*/React.createElement(Spinner, {
      scale: 4,
      s: {
        ml: 2
      }
    })), /*#__PURE__*/React.createElement(Grid.Cell, {
      align: "center",
      justify: "end"
    }, /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      align: "center",
      gap: 2
    }, /*#__PURE__*/React.createElement(Text, {
      fontSize: 3,
      display: "inline-block",
      weight: "semiBold",
      color: "textSecondary"
    }, t("NotificationDropdown.showUnread")), /*#__PURE__*/React.createElement(StyledInputToggle, {
      value: filter?.onlyUnread || false,
      onChange: value => handleFilter({
        ...filter,
        onlyUnread: value
      })
    })))), !!summary?.unreadCount && /*#__PURE__*/React.createElement(StyledButton, {
      onClick: handleReadAll,
      color: "primaryTransparent",
      fontSize: 3,
      s: {
        p: 2
      },
      fullWidth: true,
      outline: false
    }, t("NotificationDropdown.markAllAsRead")), notifications.length ? /*#__PURE__*/React.createElement(InfiniteScroll, {
      style: scrollPaneStyle,
      dataLength: notifications.length || 0,
      next: handleLoadMore,
      hasMore: hasAfter,
      loader: /*#__PURE__*/React.createElement(Dropdown.ListItem, null, /*#__PURE__*/React.createElement(Spinner, {
        s: {
          mt: 6
        },
        center: true,
        scale: 5,
        color: colors.secondary.main
      })),
      scrollableTarget: "notificationList"
    }, notifications.map(notification => /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleClick(notification);
        onItemClick(notification.id);
      },
      key: notification.id,
      textWrap: true
    }, /*#__PURE__*/React.createElement(Grid, {
      cols: 3,
      gap: 3,
      template: notification.link ? "3rem 1fr 20px 22px" : "3rem 1fr 22px",
      fullWidth: true
    }, notification.member ? /*#__PURE__*/React.createElement(Avatar, {
      user: notification.member,
      size: 10.5,
      s: {
        mt: 1
      }
    }) : /*#__PURE__*/React.createElement(Picture, {
      src: joynLogo,
      width: "3rem",
      height: "3rem",
      border: "circled",
      s: {
        mt: 1
      }
    }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
      element: "span",
      fontSize: 3,
      weight: "semiBold",
      lineHeight: 4,
      s: {
        mr: 2
      }
    }, notification.member && /*#__PURE__*/React.createElement(Link, {
      onClick: e => handleMemberClick(notification.member?.id, e),
      underline: true,
      color: "inherit",
      display: "inline",
      weight: "semiBold"
    }, notification.member.firstName, " ", notification.member.lastName), " ", /*#__PURE__*/React.createElement(Markdown, {
      text: notification.title,
      components: {
        p: _ref11 => {
          let {
            node,
            ...props
          } = _ref11;
          return /*#__PURE__*/React.createElement("span", props);
        }
      }
    })), /*#__PURE__*/React.createElement(Text, {
      element: "span",
      color: "textSecondary",
      fontSize: 3
    }, moment(notification.createdAt).local().startOf("seconds").fromNow(true), " ", t("NotificationDropdown.ago"))), notification.link && /*#__PURE__*/React.createElement(Icon, {
      src: "chevron",
      color: colors.borders.secondary,
      rotate: 90,
      s: {
        mt: 1.3
      }
    }), /*#__PURE__*/React.createElement(Button, {
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": notification.status === NotificationStatusEnum.UNREAD ? t("NotificationDropdown.markAsRead") : t("NotificationDropdown.markAsUnRead"),
      rounded: true,
      s: {
        px: 2,
        py: 2,
        mt: 0.5
      },
      color: "transparent",
      onClick: e => {
        e.stopPropagation();
        handleToggleRead(notification);
      }
    }, /*#__PURE__*/React.createElement(Dot, {
      active: notification.status === NotificationStatusEnum.UNREAD
    })))))) : /*#__PURE__*/React.createElement(Dropdown.ListItem, null, /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary"
    }, t("NotificationDropdown.noNotifications"))));
  });
};
export default NotificationDropdown;